import { Editor } from "@tiptap/react";
import { useState } from "react";

/**
 * State for {@link LinkButton}: the popover's open state plus the URL and display-text drafts.
 * Opening seeds both from the link/selection under the caret (so an existing link is edited, not
 * duplicated). Applying sets the link over the current mark range; when display text is provided it
 * replaces the linked text, otherwise the existing selection is linked in place. An empty URL
 * removes the link.
 */
export const useLinkButton = (editor: Editor | null) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");

  const isActive = editor?.isActive("link") ?? false;

  const selectedText = () => {
    if (!editor) return "";
    const { from, to } = editor.state.selection;
    return editor.state.doc.textBetween(from, to);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setUrl(editor?.getAttributes("link").href ?? "");
      setText(selectedText());
    }
    setOpen(nextOpen);
  };

  const applyLink = () => {
    if (!editor) return;
    const href = url.trim();
    const label = text.trim();
    if (!href) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      setOpen(false);
      return;
    }
    const chain = editor.chain().focus().extendMarkRange("link");
    // With display text, replace the (possibly empty) selection with linked text; without it, just
    // apply the link mark to whatever is currently selected.
    if (label) chain.insertContent({ type: "text", text: label, marks: [{ type: "link", attrs: { href } }] }).run();
    else chain.setLink({ href }).run();
    setOpen(false);
  };

  const removeLink = () => {
    editor?.chain().focus().extendMarkRange("link").unsetLink().run();
    setOpen(false);
  };

  return { open, url, setUrl, text, setText, isActive, handleOpenChange, applyLink, removeLink };
};
